// do sentry first, to catch any errors
import initSentry from './utils/sentry.js';
initSentry();

import { signInWithSaml, signInWithToken } from './utils/providers';
import { checkSessionValidity, fetchFirebaseToken, logout } from './utils/session';
import { checkForCodeGeneration } from './code';
import { renderAppGrid } from './utils/ui';
import { loadSettings } from './settings';
import { showError } from './utils/error';
import { crossFade, fadeOut, showLoadingOverlay, hideLoadingOverlay, staggerChildren, animateElement } from './utils/animate';
import { prepareAuthEntry, revealAuthShell, setAuthView } from './utils/auth-ui';
import { fetchMe, fetchMyApps } from './utils/api';
import { configureAdminUi, isPlatformAdmin, showAdminConsole, hideAdminConsole } from './admin';
import { setAppGridLoading } from './utils/loading';

export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

window.user = null;
window.loginComplete = false;

export let API_HOST = 'https://api.dhsb.org';
if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
    console.warn('Using local API');
    API_HOST = 'http://127.0.0.1:5001/slam4edu/us-central1/api';
}

const setSamlLoginLoading = (isLoading) => {
    const button = document.getElementById('login-saml');
    const label = button?.querySelector('.login-saml-text');
    const loading = button?.querySelector('.login-saml-loading');

    if (!button) {
        return;
    }

    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
    label?.classList.toggle('is-hidden', isLoading);
    loading?.classList.toggle('is-hidden', !isLoading);
};

const handleLoginAttempt = async () => {
    setSamlLoginLoading(true);
    try {
        await signInWithSaml();
        await checkForRedirect();
        await checkForCodeGeneration();
        showLoadingOverlay('Loading your account…');
        await successfulLogin();
    } catch (error) {
        console.error(error);
        const code = error?.code || '';
        if (code === 'auth/popup-closed-by-user') {
            // User dismissed the popup — not an app error
            return;
        }
        if (code === 'auth/popup-blocked') {
            showError(
                'Sign-in popup was blocked.',
                'Allow popups for account.dhsb.org, then try again.'
            );
            return;
        }
        showError('There was an error signing in with DHSB SSO.', 'Please try again later.');
    } finally {
        setSamlLoginLoading(false);
    }
};

function closeUserMenu() {
    const userDropdown = document.getElementById('user-dropdown');
    const userMenuBtn = document.getElementById('user-menu-btn');
    userDropdown.classList.add('is-hidden');
    userMenuBtn.setAttribute('aria-expanded', 'false');
}

function openUserMenu() {
    const userDropdown = document.getElementById('user-dropdown');
    const userMenuBtn = document.getElementById('user-menu-btn');
    userDropdown.classList.remove('is-hidden');
    userDropdown.classList.add('dropdown-enter');
    userMenuBtn.setAttribute('aria-expanded', 'true');
    userDropdown.querySelector('[role="menuitem"]')?.focus();
}

function configureUserMenuAccessibility() {
    const userMenuBtn = document.getElementById('user-menu-btn');
    const userDropdown = document.getElementById('user-dropdown');
    const menuItems = () => Array.from(userDropdown.querySelectorAll('[role="menuitem"]'));

    userMenuBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = !userDropdown.classList.contains('is-hidden');
        if (isOpen) {
            closeUserMenu();
        } else {
            openUserMenu();
        }
    });

    userMenuBtn.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (userDropdown.classList.contains('is-hidden')) {
                openUserMenu();
            }
        }
        if (event.key === 'Escape') {
            closeUserMenu();
        }
    });

    userDropdown.addEventListener('keydown', (event) => {
        const items = menuItems();
        const currentIndex = items.indexOf(document.activeElement);

        if (event.key === 'Escape') {
            event.preventDefault();
            closeUserMenu();
            userMenuBtn.focus();
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const next = items[(currentIndex + 1) % items.length];
            next?.focus();
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = items[(currentIndex - 1 + items.length) % items.length];
            prev?.focus();
        }

        if (event.key === 'Tab') {
            closeUserMenu();
        }
    });

    document.addEventListener('click', closeUserMenu);
}

async function configureEventListeners() {
    document.getElementById('login-saml').addEventListener('click', handleLoginAttempt);
    configureUserMenuAccessibility();
    configureAdminUi();

    document.getElementById('settings-btn').addEventListener('click', async (event) => {
        event.preventDefault();
        closeUserMenu();
        document.getElementById('settings-btn').setAttribute('aria-busy', 'true');

        try {
            await loadSettings();
            document.getElementById('settings-btn').setAttribute('aria-busy', 'false');
            const from = !document.getElementById('admin').classList.contains('is-hidden')
                ? document.getElementById('admin')
                : document.getElementById('home');
            await crossFade(from, document.getElementById('settings'));
            staggerChildren(document.getElementById('settings'), '.settings-card');
        } catch (error) {
            console.error(error);
            document.getElementById('settings-btn').setAttribute('aria-busy', 'false');
            showError('There was an error loading your account settings.', 'Please try again later.');
        }
    });

    document.getElementById('admin-btn').addEventListener('click', async (event) => {
        event.preventDefault();
        closeUserMenu();
        try {
            await showAdminConsole();
        } catch (error) {
            console.error(error);
            showError('Could not open admin.', error.message);
        }
    });

    document.getElementById('home-btn').addEventListener('click', async (event) => {
        event.preventDefault();
        await hideAdminConsole();
        await crossFade(document.getElementById('settings'), document.getElementById('home'));
        staggerChildren(document.getElementById('app-grid'), '.app-grid-item');
    });

    document.getElementById('admin-home-btn').addEventListener('click', async (event) => {
        event.preventDefault();
        await crossFade(document.getElementById('admin'), document.getElementById('home'));
        staggerChildren(document.getElementById('app-grid'), '.app-grid-item');
    });

    document.getElementById('logout-btn').addEventListener('click', async (event) => {
        event.preventDefault();
        closeUserMenu();
        document.getElementById('logout-btn').setAttribute('aria-busy', 'true');
        showLoadingOverlay('Signing out…');
        try {
            await logout();
        } finally {
            document.getElementById('logout-btn').setAttribute('aria-busy', 'false');
        }
    });

    window.addEventListener('focus', async () => {
        await checkSessionValidity();
    });
    window.addEventListener('blur', async () => {
        await checkSessionValidity();
    });
}

document.addEventListener('DOMContentLoaded', async () => {
    await configureEventListeners();

    const url = new URL(window.location.href);
    if (url.searchParams.get('logout') === 'true') {
        url.searchParams.delete('logout');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        showLoadingOverlay('Signing out…');
        try {
            await logout();
        } catch (error) {
            console.error(error);
        }
        return;
    }

    showLoadingOverlay('Loading…');
    let sessionResult = 'login';

    const sessionCheck = fetchFirebaseToken().then(async (data) => {
        await signInWithToken(data.firebaseToken, data.profile || null);
        await checkForRedirect();
        await checkForCodeGeneration();
        if (!window.generatingCode) {
            sessionResult = 'app';
        }
    }).catch((error) => {
        console.log('Session check:', error);
    });

    await Promise.all([prepareAuthEntry(), sessionCheck]);

    if (sessionResult === 'app') {
        await successfulLogin();
        return;
    }

    await hideLoadingOverlay();

    if (window.generatingCode) {
        revealAuthShell();
        return;
    }

    setAuthView('login');
    revealAuthShell();
});

async function checkForRedirect() {
    const url = new URL(window.location.href);
    const redirect = url.searchParams.get('redirect');
    if (redirect) {
        window.location.href = redirect;
    }
}

async function successfulLogin() {
    if (window.generatingCode || window.loginComplete) {
        await hideLoadingOverlay();
        return;
    }

    showLoadingOverlay('Loading your account…');

    const displayName = window.user.displayName
        || window.userProfile?.name
        || window.userProfile?.displayName
        || null;

    document.getElementById('hi-name').innerText = displayName || 'Your Account';

    const timeOfDay = new Date().getHours();
    const homeTitle = document.getElementById('home-title');
    const firstName = displayName ? displayName.split(' ')[0] : null;
    if (timeOfDay < 12) {
        homeTitle.innerText = firstName ? `Good morning, ${firstName}` : 'Good morning';
    } else if (timeOfDay < 17) {
        homeTitle.innerText = firstName ? `Good afternoon, ${firstName}` : 'Good afternoon';
    } else {
        homeTitle.innerText = firstName ? `Good evening, ${firstName}` : 'Good evening';
    }

    const userPfp = document.getElementById('user-pfp');
    const avatarFallback = document.getElementById('user-avatar-fallback');

    if (window.user && window.user.photoURL) {
        userPfp.src = new URL(window.user.photoURL).href;
        userPfp.classList.remove('is-hidden');
        avatarFallback.classList.add('is-hidden');
    } else {
        userPfp.classList.add('is-hidden');
        avatarFallback.classList.remove('is-hidden');
    }

    const auth = document.getElementById('auth');
    const app = document.getElementById('app');

    if (document.body.classList.contains('auth-ready')) {
        await fadeOut(auth);
    } else {
        auth.classList.add('is-hidden');
    }

    app.classList.remove('is-hidden');
    document.getElementById('home').classList.remove('is-hidden');
    document.getElementById('settings').classList.add('is-hidden');
    document.getElementById('admin').classList.add('is-hidden');
    setAppGridLoading(true);
    await hideLoadingOverlay();
    animateElement(document.querySelector('.page-hero'), 0);

    let apps = [];
    try {
        const [appsRes, meRes] = await Promise.all([fetchMyApps(), fetchMe()]);
        apps = appsRes.data || [];
        window.meProfile = meRes.data || null;
        const adminBtn = document.getElementById('admin-btn');
        if (adminBtn) {
            adminBtn.classList.toggle('is-hidden', !isPlatformAdmin(window.meProfile));
        }
    } catch (error) {
        console.error('Failed to load apps/profile:', error);
        apps = [];
        document.getElementById('admin-btn')?.classList.add('is-hidden');
    } finally {
        setAppGridLoading(false);
    }

    renderAppGrid(apps);
    staggerChildren(document.getElementById('app-grid'), '.app-grid-item', 80, 80);

    window.loginComplete = true;
}
